<script lang="ts" setup>
  import { useBreakpoints } from '@storefront/storefront';
  import { type PropType } from 'vue';
  import { DocumentType } from '@storefront/types';
  import type { OrderResource } from '@storefront/resources';

  type OrderDocument = {
    id: number;
    type: DocumentType;
    referenceNumber: string;
    download: string;
  }

  interface OrderPackage {
    orderId: number,
    trackingNumber: string,
    state: string | null,
    status: string | null,
    statusAt: string | null,
    branch: string | null,
    locate: string | null,
    tracking: OrderPackageTracking[],
  }

  interface OrderPackageTracking {
    state: string,
    status: string,
    datetime: string,
    branch: string,
  }

  interface OrderShipment {
    externalId: string,
    status: {
      label: string,
      description: string,
    },
    trackingUrl: string | null,
    updatedAt: string,
    parcels: OrderShipmentParcel[]
  }

  interface OrderShipmentParcel {
    externalId: string,
    status: {
      label: string,
      description: string,
    },
    trackingUrl: string | null,
    updatedAt: string,
  }

  const breakpoints = useBreakpoints();

  defineProps({
    order: { type: Object as PropType<OrderResource>, required: true },
    repayment: { type: String, required: false },
    complaint: { type: String, required: false },
    refund: { type: String, required: false },
    reorder: { type: String, required: false },
    documents: { type: Array as PropType<OrderDocument[]>, required: false, default: [] },
    packages: { type: Array as PropType<OrderPackage[]>, required: false, default: [] },
    shipments: { type: Array as PropType<OrderShipment[]>, required: false, default: [] },
  });
</script>
<template>
  <div class="order-completion">
    <SfTile large>
      <template #title>
        <div class="order-completion__header">
          <h1 v-if="breakpoints.lg" class="account__heading">
            {{ $t('SHOP.ORDER.HEADER.ORDER_NR', { number: order.number }) }}
          </h1>
          <span class="order-completion__date">
            {{ $t('SHOP.ORDER.LABEL.ORDER_DATE') }}
            <SfDateTime :value="order.orderedAt" />
          </span>
        </div>
      </template>
      <template #headerAction>
        <div class="order-completion__status">
          <SfOrderStatus :status="order.status" />
        </div>
      </template>
      <template #default>
        <div class="order-completion__products">
          <OrderDetailsLine v-for="item in order.items" :key="item.id" :item="item" :order="order" />
        </div>
        <OrderDetailsSummary :order="order" />
      </template>
    </SfTile>
    <SfTile v-if="packages.length > 0">
      <template #title>
        <h4>{{ $t('SHOP.ORDER.HEADER.SHIPMENT_TRACKING') }}</h4>
      </template>
      <template #default>
        <div class="order-packages">
          <OrderDetailsPackage v-for="shipment in packages" :shipment="shipment" />
        </div>
      </template>
    </SfTile>
    <SfTile v-if="shipments.length > 0">
      <template #title>
        <h4>{{ $t('SHOP.ORDER.HEADER.SHIPMENT_TRACKING') }}</h4>
      </template>
      <template #default>
        <div class="order-packages">
          <OrderDetailsShipment v-for="shipment in shipments" :shipment="shipment" />
        </div>
      </template>
    </SfTile>
    <SfTile v-if="documents.length > 0">
      <template #title>
        <h4>{{ $t('SHOP.ORDER.HEADER.ORDER_DOCUMENTS') }}</h4>
      </template>
      <template #default>
        <div class="order-documents">
          <ul>
            <li v-for="doc in documents">
              <a :href="doc.download" target="_blank">
                <SfIconDocument aria-hidden="true" />
                <span>{{ doc.type }}</span>
                <span>{{ doc.number }}</span>
              </a>
            </li>
          </ul>
        </div>
      </template>
    </SfTile>
    <SfTile>
      <div class="order-completion__data">
        <SfTile small>
          <template #title>
            <h4>{{ $t('SHOP.ORDER.HEADER.SHIPPING_ADDRESS') }}</h4>
          </template>
          <address class="order-completion__data-info">
            <p v-if="order.shippingCompany" class="account-billing__address-billing-comapny">{{ order.shippingCompany }}</p>
            <p v-if="order.shippingName" class="account-billing__address-billing-comapny">{{ order.shippingName }}</p>
            <p v-if="order.shippingFirstLine" class="account-shipping__address-first-line">{{ order.shippingFirstLine }}</p>
            <p v-if="order.shippingSecondLine" class="account-shipping__address-second-line">{{ order.shippingSecondLine }}</p>
            <p v-if="order.shippingThirdLine" class="account-shipping__address-third-line">{{ order.shippingThirdLine }}</p>
            <p v-if="order.countryCode" class="account-shipping__address-code">{{ order.countryCode }}</p>
            <p v-if="order.shippingEmail" class="account-shipping__address-email">{{ order.shippingEmail }}</p>
            <p v-if="order.shippingPhone" class="account-shipping__address-phone">{{ order.shippingPhone }}</p>
          </address>
        </SfTile>
        <SfTile small>
          <template #title>
            <h4 class="order-completion__data-heading">
              {{ $t('SHOP.ORDER.HEADER.BILLING_ADDRESS') }}
            </h4>
          </template>
          <address class="order-completion__data-info">
            <p v-if="order.billingName" class="account-billing__address-billing-comapny">{{ order.billingName }}</p>
            <p v-if="order.billingFirstLine" class="account-billing__address-first-line">{{ order.billingFirstLine }}</p>
            <p v-if="order.billingSecondLine" class="account-billing__address-second-line">{{ order.billingSecondLine }}</p>
            <p v-if="order.billingThirdLine" class="account-billing__address-third-line">{{ order.billingThirdLine }}</p>
            <p v-if="order.countryCode" class="account-billing__address-code">{{ order.countryCode }}</p>
            <p v-if="order.billingVatId" class="account-billing__address-nip">{{ $t('SHOP.ORDER.LABEL.VAT_ID') }} {{ order.billingVatId }}</p>
            <p v-if="order.billingEmail" class="account-billing__address-email">{{ order.billingEmail }}</p>
            <p v-if="order.billingPhone" class="account-billing__address-phone">{{ order.billingPhone }}</p>
          </address>
        </SfTile>
        <SfTile v-if="order.shippingOptionName" small>
          <template #title>
            <h4 class="order-completion__data-heading">
              {{ $t('SHOP.ORDER.HEADER.SHIPPING_METHOD') }}
            </h4>
          </template>
          <div class="order-completion__data-info">
            <p class="order-completion__shipping-name">{{ order.shippingOptionName }}</p>
            <p v-if="order.shippingPointId" class="order-completion__shipping-name">
              {{ order.shippingPointId }}
              <span v-if="order.shippingPointName">({{ order.shippingPointName }})</span>
            </p>
          </div>
        </SfTile>
        <SfTile v-if="order.billingMethodOptionName" small>
          <template #title>
            <h4 class="order-completion__data-heading">
              {{ $t('SHOP.ORDER.HEADER.PAYMENT_METHOD') }}
            </h4>
          </template>
          <div class="order-completion__data-info">
            <p class="order-completion__billing-name">{{ order.billingMethodOptionName }}</p>
          </div>
        </SfTile>
      </div>
    </SfTile>
    <div class="order-completion__button-wrapper">
      <SfButton v-if="reorder" :href="reorder" method="POST" primary>
        {{ $t('SHOP.ORDER.ACTION.ORDER_AGAIN') }}
      </SfButton>
      <SfButton v-if="repayment" :href="repayment" primary>
        {{ $t('SHOP.ORDER.ACTION.PAY_FOR_ORDER') }}
      </SfButton>
      <SfButton v-if="complaint" :href="complaint" primary>
        {{ $t('SHOP.ORDER.ACTION.COMPLAINT') }}
      </SfButton>
      <SfButton v-if="refund" :href="refund" primary>
        {{ $t('SHOP.ORDER.ACTION.REFUND') }}
      </SfButton>
      <slot name="actions" />
    </div>
  </div>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-order-details-old-price: var(--sf-product-price-catalog-color, rgb(200, 0, 0));
    --sf-input-number-min-width: 114px;
  }

  .order-documents {
    ul {
      display: grid;
      grid-template-columns: 1rem auto 1fr;
      column-gap: 1rem;
      row-gap: 0.5rem;
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        display: contents;

        a {
          display: contents;

          span {
            font-size: 0.875rem;
            line-height: 1rem;
          }
        }
      }
    }
  }

  .order-packages {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    gap: 1rem;
  }

  .order-completion {
    display: flex;
    flex-direction: column;
    row-gap: 2rem;

    &__products,
    &__product-content,
    &__header {
      display: flex;
      flex-direction: column;
    }

    &__heading {
      word-break: break-all;
      text-wrap: balance;
    }

    &__products {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__date {
      font-size: 0.875rem;
      color: var(--sf-account-color-dark-grey);
      font-weight: var(--font-weight-semi);
    }

    &__status {
      font-weight: var(--font-weight-semi);
      color: var(--sf-status-color-success);
      flex-shrink: 0;
    }

    &__data {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      align-items: flex-start;
      gap: 2rem;

      .placeholder {
        --sf-data-placeholder-padding: 0.375rem
      }
    }

    &__data-info p {
      font-size: 0.875rem;
    }

    &__action {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
    }

    &__button-wrapper {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.75rem;
    }

    &__product-price {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      align-items: flex-end;
    }
  }

  @include media.query(xxl) {
    .order-completion__data {
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
    }
  }

  @include media.query(xl) {
    .order-completion__data {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @include media.query(lg) {
    .order-completion {
      row-gap: 1rem;

      .tile > .tile__content > .tile__header {
        flex-wrap: wrap;
      }

      &__heading {
        font-size: 1.375rem;
      }

      &__product {
        padding-bottom: 1rem;
      }

      &__data-heading {
        font-size: 1rem;
      }

      &__button-wrapper {
        padding: 0 0.625rem;
      }
    }
  }

  @include media.query(sm) {
    .order-completion {
      &__data {
        grid-template-columns: 1fr;
      }

      &__product-heading {
        font-size: 1rem;
      }

      &__product-price {
        font-size: 0.875rem;
      }

      &__wrapper {
        gap: 0.625rem;
        padding: 0.625rem 0.125rem;
      }

      &__content {
        gap: 0.625rem;
      }

      &__button-wrapper .btn {
        flex-grow: 1;
      }
    }
  }
</style>
